import { isAuthenticated } from './auth';
import { parseRouteFromLocation } from './appHistory';

export const NAV_SNAPSHOT_KEY = 'craftmyparty_active_session_v1';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function readNavSnapshot() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(NAV_SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw);
    if (!snap || typeof snap !== 'object') return null;
    if (snap.savedAt && Date.now() - snap.savedAt > MAX_AGE_MS) return null;
    return snap;
  } catch (_) {
    return null;
  }
}

export function writeNavSnapshot(state) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      NAV_SNAPSHOT_KEY,
      JSON.stringify({ ...state, savedAt: Date.now() })
    );
  } catch (_) {}
}

export function clearNavSnapshot() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(NAV_SNAPSHOT_KEY);
  } catch (_) {}
}

/**
 * Read sessionStorage once before first paint so refresh does not flash the
 * dashboard while React still has default state (showLanding=true).
 */
export function getInitialAppRouteState() {
  const snap = readNavSnapshot();
  const fromHash = parseRouteFromLocation();
  const authed = typeof window !== 'undefined' && isAuthenticated();
  // Merge URL route (/wizard/plan or legacy #/wizard/plan) with snapshot so
  // refresh still restores the saved plan when ?plan= is only in sessionStorage.
  const routeSource = authed && (fromHash || snap)
    ? {
        ...(snap || {}),
        ...(fromHash || {}),
        planId: fromHash?.planId || snap?.planId || null,
        showLanding: fromHash?.showLanding ?? snap?.showLanding,
        currentScreen: fromHash?.currentScreen || snap?.currentScreen || 'input',
        currentView: fromHash?.currentView || snap?.currentView || 'dashboard'
      }
    : snap;

  if (!authed || !routeSource) {
    return {
      showLanding: true,
      currentView: 'dashboard',
      currentScreen: 'input',
      bootRestore: null,
      restorePlanId: null,
      restoreScreen: null,
      optimisticShell: false,
      optimisticWizard: false
    };
  }

  const currentView = routeSource.currentView || 'dashboard';
  const currentScreen = routeSource.currentScreen || 'input';
  let planId = routeSource.planId || snap?.planId || null;
  if (!planId && typeof window !== 'undefined') {
    try {
      planId = sessionStorage.getItem('current_plan_id') || null;
    } catch (_) {}
  }
  const showLandingFromSource = routeSource.showLanding;

  const wizardScreen = ['plan', 'carousel', 'loading', 'loadingDetails'].includes(currentScreen);
  const inWizard = showLandingFromSource === false
    || wizardScreen
    || Boolean(planId && currentScreen !== 'input');
  const showLanding = showLandingFromSource === true
    ? true
    : showLandingFromSource === false
      ? false
      : !inWizard;
  const needsPlanHydrate = Boolean(planId && inWizard);

  return {
    showLanding,
    currentView,
    currentScreen,
    bootRestore: needsPlanHydrate ? 'plan' : null,
    restorePlanId: needsPlanHydrate ? planId : null,
    restoreScreen: needsPlanHydrate ? currentScreen : null,
    optimisticShell: showLanding && !needsPlanHydrate,
    optimisticWizard: needsPlanHydrate
  };
}
